import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';

const CHARACTER_DELAY_MS = 18;

function splitLastWord(text: string) {
  const start = /\S+$/u.exec(text)?.index ?? text.length;
  return { prefix: text.slice(0, start), word: text.slice(start) };
}

function HiddenContinuation({ measurement = false }: { measurement?: boolean }) {
  return (
    <span aria-hidden="true" className="guest-answer-continuation" data-guest-decorative-preview={measurement ? undefined : ''}>
      <span /><span /><span />
    </span>
  );
}

/** Only the explicitly public preview is rendered; the continuation contains no text. */
export function GuestAnswerExcerpt({ preview }: { preview: string }) {
  const source = preview.trim().replace(/[.\u2026]+$/u, '');
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const layout = useRef<HTMLSpanElement>(null);
  const measurementText = useRef<HTMLSpanElement>(null);
  const measurementWord = useRef<HTMLSpanElement>(null);
  const measurementTail = useRef<HTMLSpanElement>(null);
  const [fit, setFit] = useState({ source, text: source });
  const text = fit.source === source ? fit.text : source;
  const characters = Array.from(text);
  const { prefix, word } = splitLastWord(text);
  const [reveal, setReveal] = useState({ source, count: 0 });
  const progress = useRef({ source, count: 0, complete: false });
  const count = reveal.source === source ? reveal.count : 0;
  const typed = reduceMotion ? text : characters.slice(0, count).join('');
  const complete = reduceMotion || (reveal.source === source && count >= characters.length);

  useLayoutEffect(() => {
    const element = layout.current;
    const candidate = measurementText.current;
    const finalWord = measurementWord.current;
    const tail = measurementTail.current;
    if (!element || !candidate || !finalWord || !tail) return;
    const measure = (value: string) => {
      const parts = splitLastWord(value);
      candidate.textContent = parts.prefix;
      finalWord.textContent = parts.word;
    };
    const fitToTwoLines = () => {
      const lineHeight = Number.parseFloat(window.getComputedStyle(element).lineHeight);
      if (!element.getBoundingClientRect().width || !Number.isFinite(lineHeight)) return;
      const available = lineHeight * 2 + 0.5;
      const letters = Array.from(source);
      let lower = 0;
      let upper = letters.length;
      while (lower < upper) {
        const middle = Math.ceil((lower + upper) / 2);
        measure(letters.slice(0, middle).join('').trimEnd());
        const fitsWidth = tail.getBoundingClientRect().width <= element.getBoundingClientRect().width;
        if (fitsWidth && element.getBoundingClientRect().height <= available) lower = middle;
        else upper = middle - 1;
      }
      const next = letters.slice(0, lower).join('').trimEnd();
      measure(next);
      setFit((current) => current.source === source && current.text === next ? current : { source, text: next });
    };
    fitToTwoLines();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fitToTwoLines);
    observer?.observe(element.parentElement!);
    window.addEventListener('resize', fitToTwoLines);
    let disposed = false;
    void document.fonts?.ready.then(() => { if (!disposed) fitToTwoLines(); });
    document.fonts?.addEventListener('loadingdone', fitToTwoLines);
    return () => {
      disposed = true;
      observer?.disconnect();
      window.removeEventListener('resize', fitToTwoLines);
      document.fonts?.removeEventListener('loadingdone', fitToTwoLines);
    };
  }, [source]);

  useEffect(() => {
    if (progress.current.source !== source) progress.current = { source, count: 0, complete: false };
    if (reduceMotion || progress.current.complete || progress.current.count >= characters.length) {
      progress.current.complete = true;
      setReveal({ source, count: characters.length });
      return;
    }
    setReveal({ source, count: progress.current.count });
    const interval = window.setInterval(() => {
      progress.current.count += 1;
      setReveal({ source, count: progress.current.count });
      if (progress.current.count >= characters.length) {
        progress.current.complete = true;
        window.clearInterval(interval);
      }
    }, CHARACTER_DELAY_MS);
    return () => window.clearInterval(interval);
  }, [characters.length, reduceMotion, source]);

  return (
    <blockquote className="guest-answer-excerpt">
      <span aria-hidden="true" className="guest-answer-quote">“</span>
      <p className="guest-answer-typewriter">
        <span aria-hidden="true" className="guest-answer-text-layout" ref={layout}>
          <span ref={measurementText}>{prefix}</span><span className="guest-answer-inline-tail" ref={measurementTail}><span ref={measurementWord}>{word}</span>...<HiddenContinuation measurement /></span>
        </span>
        <span aria-hidden="true" className="guest-answer-text-visible" data-guest-answer-preview>
          {typed.slice(0, prefix.length)}<span className="guest-answer-inline-tail">{typed.slice(prefix.length)}<span className="guest-answer-tail-reveal" data-complete={complete}>{complete ? '...' : ''}<HiddenContinuation /></span></span>
        </span>
        <span className="sr-only">{text}...</span>
      </p>
    </blockquote>
  );
}
